import mapboxgl from 'mapbox-gl'

import { DEFAULT_BORDER_COLOR, DEFAULT_MAIN_COLOR } from 'src/components/ColorDot'

import { IFeature, IStationInformation, ISelectedStation, IStationStatus } from './types'
import { addMapImage } from './utils'

const SOURCE = 'docked-bikes'
const DISPLAY_LOGO_BREAKPOINT = 17

const getDataFromGBFSFeeds = (
  stationInformations: IStationInformation[],
  stationStatuses: IStationStatus[],
  provider: string
): IFeature[] =>
  stationStatuses.map((station: IStationStatus) => {
    const information = stationInformations.find((info: IStationInformation) => info.station_id === station.station_id)
    const { name = '', lon = 0, lat = 0 } = information || {}

    return {
      type: 'Feature',
      properties: {
        numBikesAvailable: station.num_bikes_available,
        numDocksAvailable: station.num_docks_available,
        provider,
        address: name,
        lastReported: station.last_reported,
      },
      geometry: {
        type: 'Point',
        coordinates: [lon, lat],
      },
    }
  })

export const addDockedBikes = async (
  currentMap: mapboxgl.Map,
  onMouseEnter: () => void,
  onMouseLeave: () => void,
  setSelectedStation: (station: ISelectedStation) => void,
  gbfsData: any
) => {
  for (const providerId of Object.keys(gbfsData)) {
    const features: IFeature[] = getDataFromGBFSFeeds(
      gbfsData[providerId].data.station_information,
      gbfsData[providerId].data.station_status,
      providerId
    )

    currentMap.addSource('docked-bikes', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features,
      },
    })

    const provider = gbfsData[providerId].provider

    currentMap.addLayer({
      id: 'docked-bikes-layer',
      source: SOURCE,
      type: 'circle',
      paint: {
        'circle-radius': {
          base: 2,
          stops: [
            [1, 1],
            [14, 6],
            [15, 7],
            [DISPLAY_LOGO_BREAKPOINT, 14],
          ],
        },
        'circle-color': provider.colors && provider.colors.primary ? provider.colors.primary : DEFAULT_MAIN_COLOR,
        'circle-stroke-color':
          provider.colors && provider.colors.secondary ? provider.colors.secondary : DEFAULT_BORDER_COLOR,
        'circle-stroke-width': 0,
      },
    })

    if (provider.logo) {
      await addMapImage(currentMap, provider.logo, providerId)
      currentMap.addLayer({
        id: 'docked-bikes-details',
        source: SOURCE,
        type: 'symbol',
        minzoom: DISPLAY_LOGO_BREAKPOINT,
        layout: {
          'icon-image': providerId,
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-ignore-placement': false,
        },
      })
    }

    currentMap.on('mouseenter', 'docked-bikes-details', onMouseEnter)
    currentMap.on('mouseleave', 'docked-bikes-details', onMouseLeave)

    const displayBikeStationPopup = (layer: string) => async (e: any) => {
      if (e.features && e.target.getZoom() >= DISPLAY_LOGO_BREAKPOINT) {
        const stations = currentMap.queryRenderedFeatures(e.point, {
          layers: [layer],
        })
        if (stations.length && stations[0]) {
          const station = stations[0]
          if (station.properties) {
            setSelectedStation({
              coordinates: e.lngLat,
              station: station as unknown as IFeature,
              provider,
            })
          }
        }
      }
    }
    currentMap.on('click', 'docked-bikes-layer', displayBikeStationPopup('docked-bikes-layer'))
    currentMap.on('click', 'docked-bikes-details', displayBikeStationPopup('docked-bikes-details'))
  }
}
